/**
 * Pull the most useful message out of a failed API call so callers can surface the real backend reason.
 * Handles ASP.NET ProblemDetails and ValidationProblemDetails, falling back to the supplied message.
 */
export const extractApiErrorMessage = (error: any, fallback: string): string =>
  extractProblemDetailsMessage(error?.response?.data, fallback);

/**
 * The same extraction applied to an already-read response body, for callers that read the response themselves
 * (`fetch`) instead of receiving an axios error.
 */
export const extractProblemDetailsMessage = (data: any, fallback: string): string =>
  extractProblemDetailsMessages(data, fallback).join(" ");

/**
 * Every message in a response body, one per validation failure, for callers that list them rather than run them
 * together in one line.
 */
export const extractProblemDetailsMessages = (data: any, fallback: string): string[] => {
  if (data && typeof data === "object") {
    if (data.errors && typeof data.errors === "object") {
      const messages = Object.values(data.errors)
        .flat()
        .filter((m): m is string => typeof m === "string" && m.trim().length > 0);
      if (messages.length) return messages;
    }
    for (const field of ["detail", "title", "message"] as const) {
      const value = data[field];
      if (typeof value === "string" && value.trim().length > 0) return [value];
    }
  }
  if (typeof data === "string" && data.trim().length > 0) return [data];
  return [fallback];
};

/** A failed API call that keeps each of the backend's messages, so a caller can list them one per line. */
export class ApiErrorWithMessages extends Error {
  readonly messages: string[];

  constructor(messages: string[]) {
    super(messages.join(" "));
    this.name = "ApiErrorWithMessages";
    this.messages = messages;
  }
}

/** Wraps a failed axios call in an {@link ApiErrorWithMessages}. */
export const apiErrorWithMessages = (error: any, fallback: string): ApiErrorWithMessages =>
  new ApiErrorWithMessages(extractProblemDetailsMessages(error?.response?.data, fallback));
